import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { FileText } from "@phosphor-icons/react";
import { formatIdDate } from "@/lib/format";
import StatusPill from "@/components/StatusPill";

/**
 * One row of the SPB table. Presentation-only, all data comes from props.
 *
 * Props:
 *   s: the SPB doc from the API. Expected keys documented in `SPBList.jsx`.
 */
export default function SpbListRow({ s }) {
  const extraPelangganCount = (s.pelanggans || []).length - 1;
  const jumlahTermuat = s.jumlah_termuat;
  const jumlahBerbeda = jumlahTermuat != null && jumlahTermuat !== ""
    && Number(jumlahTermuat) !== Number(s.jumlah);

  return (
    <tr className="border-b border-border hover:bg-secondary/50">
      <td className="p-3 font-mono font-bold">
        <Link to={`/spb/${s.id}`} className="hover:underline text-primary" data-testid={`spb-link-${s.no_spb}`}>
          {s.no_spb}
        </Link>
      </td>
      <td className="p-3 font-mono">{formatIdDate(s.tanggal)}</td>
      <td className="p-3 font-mono text-xs">{s.slot_antrian || <span className="text-muted-foreground">-</span>}</td>
      <td className="p-3">{s.distrik_kode} ({s.distrik_nama})</td>
      <td className="p-3" data-testid={`spb-pelanggan-${s.id}`}>
        {s.nama_pelanggan ? (
          <div className="flex items-center gap-1.5">
            <span title={(s.pelanggans || []).map((p) => `${p.nama} — ${p.alamat}`).join("\n") || s.nama_pelanggan}>
              {s.nama_pelanggan}
            </span>
            {extraPelangganCount > 0 && (
              <span
                className="inline-block border border-blue-400/60 bg-blue-50 text-blue-800 text-[9px] font-bold uppercase tracking-wider px-1 py-0.5"
                title={(s.pelanggans || []).slice(1).map((p) => `+ ${p.nama}`).join("\n")}
              >
                +{extraPelangganCount}
              </span>
            )}
          </div>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>
      <td className="p-3">{s.produk_nama}</td>
      <td className="p-3 font-mono font-bold">{s.no_polisi}</td>
      <td className="p-3">{s.nama_supir}</td>
      <td className="p-3 font-mono">{s.jumlah} {s.satuan}</td>
      <td className="p-3 font-mono" data-testid={`spb-jumlah-termuat-${s.id}`}>
        {jumlahTermuat != null && jumlahTermuat !== "" ? (
          <span className={jumlahBerbeda ? "text-orange-600 font-bold" : ""}>
            {jumlahTermuat} {s.satuan}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>
      <td className="p-3"><StatusPill status={s.status} /></td>
      <td className="p-3" data-testid={`spb-sync-${s.id}`}>
        <SyncBadge s={s} />
      </td>
      <td className="p-3">
        <Link to={`/spb/${s.id}`}>
          <Button size="sm" variant="outline" className="rounded-none">
            <FileText size={14} className="mr-1" /> Detail
          </Button>
        </Link>
      </td>
    </tr>
  );
}

function SyncBadge({ s }) {
  if (s.sync_status === "matched") {
    return (
      <span
        className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800 border border-emerald-300"
        title={s.sync_synced_at
          ? `Sinkron: ${new Date(s.sync_synced_at).toLocaleString("id-ID")}`
          : "Match dengan upload realisasi"}
      >
        ✓ Match
      </span>
    );
  }
  if (s.sync_status === "mismatch") {
    return (
      <span
        className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-red-100 text-red-800 border border-red-300"
        title={(s.sync_mismatch_reasons || []).join(", ") || "Mismatch"}
      >
        ✗ Mismatch
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] uppercase tracking-wider bg-secondary text-muted-foreground border border-border"
      title="Belum sync dengan upload realisasi"
    >
      — Belum
    </span>
  );
}
